/**
 * Address Service
 * 
 * Frontend service for Google-powered address autocomplete and validation.
 * Communicates with backend endpoints that keep Google API keys secure.
 * 
 * Features:
 * - Address autocomplete suggestions
 * - Address validation and standardization
 * - Save correct address for identity protection
 * - Get saved correct address
 * 
 * Cost Controls:
 * - Only calls autocomplete after 3+ characters
 * - Debounce handled in component (300ms)
 * - Backend rate limiting enforced
 */

import { apiFetchJson } from "./apiClient";

/**
 * Get address autocomplete suggestions.
 * 
 * @param {object} params
 * @param {string} params.input - User input (min 3 chars)
 * @param {string} [params.sessionToken] - Session token for Google billing optimization
 * @param {string} [params.country="US"] - Country code
 * @param {AbortSignal} [params.signal] - AbortController signal for request cancellation
 * @returns {Promise<{success: boolean, suggestions: Array}>}
 */
export async function autocompleteAddress({ input, sessionToken, country = "US", signal }) {
  return apiFetchJson("/api/address/autocomplete", {
    method: "POST",
    signal,
    body: JSON.stringify({
      input,
      session_token: sessionToken,
      country,
    }),
  });
}

/**
 * Validate and standardize an address.
 * 
 * @param {object} params
 * @param {string} params.rawAddress - The address to validate
 * @param {string} [params.placeId] - Google Place ID if from autocomplete
 * @param {string} [params.country="US"] - Country code
 * @param {string} [params.streetLine2=""] - Apt, Suite, Unit (optional)
 * @param {AbortSignal} [params.signal] - AbortController signal for request cancellation
 * @returns {Promise<AddressValidationResponse>}
 */
export async function validateAddress({
  rawAddress,
  placeId = null,
  country = "US",
  streetLine2 = "",
  signal,
}) {
  return apiFetchJson("/api/address/validate", {
    method: "POST",
    signal,
    body: JSON.stringify({
      raw_address: rawAddress,
      place_id: placeId,
      country,
      street_line_2: streetLine2,
    }),
  });
}

/**
 * Save the correct address for identity protection.
 * 
 * @param {object} addressPayload - Full address payload with verification data
 * @returns {Promise<{success: boolean, message: string, correct_address: object}>}
 */
export async function saveCorrectAddress(addressPayload) {
  return apiFetchJson("/api/address/save-correct-address", {
    method: "POST",
    body: JSON.stringify(addressPayload),
  });
}

/**
 * Get the user's saved correct address.
 * 
 * @returns {Promise<{success: boolean, correct_address: object|null}>}
 */
export async function getCorrectAddress() {
  return apiFetchJson("/api/address/correct-address", {
    method: "GET",
  });
}

/**
 * Get address service configuration.
 * 
 * @returns {Promise<{success: boolean, google_enabled: boolean, default_country: string, manual_entry_allowed: boolean}>}
 */
export async function getAddressConfig() {
  return apiFetchJson("/api/address/config", {
    method: "GET",
  });
}

export default {
  autocompleteAddress,
  validateAddress,
  saveCorrectAddress,
  getCorrectAddress,
  getAddressConfig,
};
